// src/pages/GCDesk.js
//
// THE GENERAL CHAIRPERSON'S DESK (weekend plan, batch 3).
//
// WHAT THIS PAGE IS FOR, and it is not "Claims but wider". A Local Chairperson's question is "what
// needs me today" - src\pages\LCDesk.js answers it. A General Chairperson's question is different
// and the platform has never answered it: WHAT IS NOBODY LOOKING AT. Three of the four sections
// below are counts of absence, and every one of them reads as a clear screen when it is wrong:
//
//   a claim routed nowhere        no Local Chairperson can see it. Only he can fix it.
//   a seat nobody holds           it resolved perfectly, to an empty chair. Ruling 60 vests that
//                                 work and that authority in him.
//   an arbitration case unfiled   the Board never hears it.
//
// THE ORDERING AND THE COUNTING LIVE IN src/data/gcDesk.js, NOT HERE, and are proved by
// scripts/test-gc-desk.mjs - 80 assertions, four of them written by deliberately breaking the
// module and confirming the suite caught it. This file renders what that module returns and adds
// nothing to it.
//
// THE ONE THING ON THIS PAGE THAT WRITES. The reassign control changes a claim's scopeKey. That is
// permitted to a General Chairperson by the DEPLOYED rules - firestore.rules, gcEdit() - and to
// nobody else, so this batch needs no rules deploy. The payload is reassignPatch() in gcDesk.js:
// three fields, one of which (localCommittee) exists solely to keep verifier check CLM-2 green.
//
// WHAT IS DELIBERATELY NOT DUPLICATED HERE. The readiness surface is src\pages\Readiness.js and it
// stays there. This page links to it and does not re-implement it: a fact in two places is a defect
// (SS XI), and a second copy of that logic would drift from the first within a week.

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp, arrayUnion, onSnapshot, collection, query, where } from 'firebase/firestore';
import { activate } from '../data/a11yActivate';   // pass 2: a keyboard reaches these rows
import { subscribeVisibleClaims } from '../data/claimQueries';
import ClaimTally from '../components/ClaimTally';   // section 493: the read-only claim tally
import { partitionGC, reassignCandidates, reassignPatch, DOCKET_SOON_DAYS, forwardedDisciplineToGC } from '../data/gcDesk';
import { subscribeVisibleCases } from '../data/disciplineQueries';   // T5 / D-18: forwarded discipline cases
import DeskClocksPanel from '../components/DeskClocksPanel';   // master list item 6 (Bob 2026-10-14)
import { withdrawnRows, deskClockRows, boardRows } from '../data/deskClocks';
import { claimPatch } from '../data/claimWrite';
import AssignmentsPanel from '../components/AssignmentsPanel';   // G8.2 - ruling 17 assignments reach the desk
import ContinuityPlanPanel from '../components/ContinuityPlanPanel';   // 6a-11 - ruling 18, the plan offered four ways
import LetterheadPanel from '../components/LetterheadPanel';            // ruling 229 - the committee's letterhead
import SignoffPanel from '../components/SignoffPanel';                  // ruling 230 - the officer's own sign-off
import GcAgreementAdmin from '../components/GcAgreementAdmin';   // section 530: the GC's working matrices
import GcRequiredReading from '../components/GcRequiredReading';   // section 520: the DFR white paper, at onboarding
import WarRoomActionsDesk from '../components/WarRoomActionsDesk';   // section 532
import PresentmentDirectoryPanel from '../components/PresentmentDirectoryPanel';   // Record 610 (ruling 262/259): the GC's directory
import ServiceSettingsPanel from '../components/ServiceSettingsPanel';   // section 539: how claims are served, per property
import ArbitrariesPanel from '../components/ArbitrariesPanel';   // item 27 / ISS-31: the committee's arbitraries
import ProposalCostPanel from '../components/ProposalCostPanel';   // item 27 / ISS-30: what a proposal really costs
import SeatsPanel from '../components/SeatsPanel';   // WO-001 S3 - the seats as the spine holds them
import RulingRef from '../components/RulingRef';   // ruling 168: the number behind Why? for a member, [R-nn] for an officer
import S44DeskPanel from '../components/S44DeskPanel';   // Record 593 (ruling 205 audit): served §44 notices not yet acknowledged
import MemberStandingFlags from '../components/MemberStandingFlags';   // Bob 2026-10-25: the S&T's private standing marks reach the GC
import useSeatsOfRecord from '../hooks/useSeatsOfRecord';   // WO-001 S3 - the roster is the spine's projection

const C = {
  panel: '#0B1626', line: '#2E4A6B', dim: '#8AA4BF',
  accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2',
};

function Block({ title, subtitle, tone, count, children }) {
  const colour = tone === 'unavailable' || tone === 'neutral' ? C.dim : tone === 'bad' ? C.bad : tone === 'warn' ? C.accent : C.ok;
  return (
    <div style={{ border: '1px solid ' + C.line, background: C.panel, marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    padding: '10px 14px', borderBottom: '1px solid ' + C.line, gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{title}</div>
          {subtitle ? <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>{subtitle}</div> : null}
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: colour, whiteSpace: 'nowrap' }}>
          {tone === 'unavailable' ? 'NOT BUILT YET' : count}
        </div>
      </div>
      <div style={{ padding: '8px 14px 12px', fontSize: 12, color: C.dim, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

// A claim awaiting jurisdiction, with the control that gives it one.
function UnroutedRow({ x, roster, user, onOpen }) {
  const c = x.claim;
  const [target, setTarget] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  const candidates = useMemo(() => reassignCandidates(c, roster), [c, roster]);
  // Whether the craft/railroad narrowing actually narrowed. Said out loud, because a picker showing
  // the whole roster when the officer expected two seats is a fact he should have, not a mystery.
  const narrowed = candidates.length < (roster ? roster.length : candidates.length);

  const save = async () => {
    const patch = reassignPatch(target);
    if (!patch) { setErr('That is not a Local Chairperson scope key. Nothing was written.'); return; }
    setSaving(true); setErr(''); setDone('');
    try {
      await updateDoc(doc(db, 'claims', c.docId || c.id), claimPatch({
        ...patch,
        // Ruling 58: a jurisdiction change is a thing that was DONE TO a member's claim, by a named
        // officer, at a time. arrayUnion, not a read-modify-write of the array - the note history is
        // one field and the rules layer cannot see inside it (the defect fixed on 2026-08-28).
        notes: arrayUnion({
          text: 'Routing assigned to ' + patch.scopeKey + '. The work location did not resolve to a seat, '
              + 'so this claim was captured as UNROUTED and assigned by the General Chairperson.',
          addedBy: user?.name || user?.email || 'Unknown',
          role: user?.role || 'general_chair',
          addedById: user?.uid || '',
          addedAt: new Date().toISOString(),
        }),
        updatedAt: serverTimestamp(),
      }, user));
      setDone('Assigned to ' + patch.scopeKey + '.');
    } catch (e) {
      console.error('[GCDesk] reassign failed:', e);
      // A rejected write must never look like a saved one (PD-5). The control stays as it was, with
      // the selection intact, and says what happened.
      setErr('NOT assigned: ' + ((e && e.message) || 'the write was rejected')
           + '. The claim is unchanged and still unrouted.');
    } finally { setSaving(false); }
  };

  const window = x.left === null
    ? <span style={{ color: C.dim }}>no filing window on record</span>
    : x.left < 0
      ? <span style={{ color: C.bad }}>window passed {Math.abs(x.left)}d ago</span>
      : <span style={{ color: x.left <= 10 ? C.accent : C.dim }}>{x.left}d of window left</span>;

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div {...activate(onOpen ? () => onOpen(c.docId || c.id) : null, 'Open claim ' + (c.id || c.docId || 'with no number'))}
             style={{ cursor: onOpen ? 'pointer' : 'default' }}>
          <span style={{ color: C.text }}>{c.id || c.docId || '(no claim number)'}</span>
          <div style={{ fontSize: 11, color: C.dim }}>
            {[c.craft, c.railroad, c.workLocation].filter(Boolean).join('  -  ') || 'craft, railroad and location not on the claim'}
            {x.age !== null ? '  -  ' + x.age + 'd on the platform' : '  -  age unknown'}
          </div>
        </div>
        <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{window}</div>
      </div>

      {done
        ? <div style={{ color: C.ok, fontSize: 11, marginTop: 6 }}>{done} It leaves this queue on the next refresh.</div>
        : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            <select aria-label="Assign to a Local Chairperson" value={target} onChange={e => setTarget(e.target.value)} disabled={saving}
                    style={{ background: '#0D1B2A', color: C.text, border: '1px solid ' + C.line,
                             padding: '4px 6px', fontSize: 11, maxWidth: 460 }}>
              <option value="">Assign to a Local Chairperson...</option>
              {candidates.map(lc => <option key={lc.scopeKey} value={lc.scopeKey}>{lc.label}</option>)}
            </select>
            <button onClick={save} disabled={!target || saving}
                    style={{ background: target && !saving ? C.accent : '#2A3646', color: '#10202F',
                             border: 'none', padding: '5px 10px', fontSize: 11, fontWeight: 700,
                             cursor: target && !saving ? 'pointer' : 'default' }}>
              {saving ? 'Assigning...' : 'Assign'}
            </button>
            <span style={{ fontSize: 10, color: C.dim }}>
              {narrowed
                ? 'Narrowed to this claim’s craft and railroad.'
                : 'Showing every seat — this claim carries no craft/railroad to narrow on, or none matched.'}
            </span>
          </div>
        )}
      {err ? <div style={{ color: C.bad, fontSize: 11, marginTop: 6 }}>{err}</div> : null}
    </div>
  );
}

// R-6b (2026-09-21). THE ROW FOR A CLAIM A LOCAL CHAIRPERSON SENT UP.
//
// It deliberately does NOT offer an action. Elevation is the hinge of the ladder (claimLadder.js):
// at gc_review the matter has moved up, and what happens next is the General Chairperson opening the
// claim and working it - not a button on a summary screen that advances a member's claim by one
// click from a list. Clicking the row opens it. That is the whole interaction.
function ElevatedRow({ x, onOpen }) {
  const c = x.claim;
  const window = x.left === null || x.left === undefined
    ? <span style={{ color: C.dim }}>no filing window on record</span>
    : x.left < 0
      ? <span style={{ color: C.bad }}>window passed {Math.abs(x.left)}d ago</span>
      : <span style={{ color: x.left <= 10 ? C.accent : C.dim }}>{x.left}d of window left</span>;

  return (
    <div style={{ padding: '8px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div {...activate(onOpen ? () => onOpen(c.docId || c.id) : null, 'Open claim ' + (c.id || c.docId || 'with no number'))}
             style={{ cursor: onOpen ? 'pointer' : 'default' }}>
          <span style={{ color: C.text }}>{c.id || c.docId || '(no claim number)'}</span>
          <div style={{ fontSize: 11, color: C.dim }}>
            {[c.craft, c.railroad, c.workLocation].filter(Boolean).join('  -  ') || 'craft, railroad and location not on the claim'}
            {x.filedDaysAgo !== null && x.filedDaysAgo !== undefined
              ? '  -  filed ' + x.filedDaysAgo + 'd ago'
              : '  -  filing date unknown'}
          </div>
          {/* R-6: THE HONEST GAP. elevatedToGC returns waitingUnknown because no claim carries an
              elevatedAt. How long this has sat on the General Chairperson's desk is NOT KNOWN, and
              the screen says so rather than showing filing age in a column a reader would take for
              waiting time. A wrong number here is worse than no number: it is the number an officer
              would defend himself with. */}
          {x.waitingUnknown ? (
            <div style={{ fontSize: 10, color: C.dim, fontStyle: 'italic' }}>
              How long it has been waiting on you is not recorded - nothing stamps a claim when it is
              sent up. The age above is the FILING age, not waiting time.
            </div>
          ) : null}
        </div>
        <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{window}</div>
      </div>
    </div>
  );
}

function SeatRow({ s }) {
  const tone = s.state === 'VACANT' ? C.bad : s.state === 'NOT_OF_RECORD' ? C.accent : C.dim;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0',
                  borderBottom: '1px dotted ' + C.line }}>
      <div>
        <span style={{ color: C.text }}>{s.scopeKey}</span>
        <span style={{ color: tone, fontSize: 11 }}>{'  -  ' + s.state.replace('_', ' ').toLowerCase()}</span>
        <div style={{ fontSize: 11, color: C.dim }}>{s.label}</div>
      </div>
      <div style={{ fontSize: 11, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <div style={{ color: s.claims.length ? C.accent : C.dim }}>{s.claims.length} claim(s)</div>
        <div style={{ color: C.dim }}>
          {s.soonestWindowDays === null ? 'no window on record' : 'soonest window ' + s.soonestWindowDays + 'd'}
        </div>
      </div>
    </div>
  );
}

function DocketRow({ x }) {
  const r = x.row;
  return (
    <div style={{ padding: '6px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <span style={{ color: C.text }}>{r.case_id}</span>
          <span style={{ color: C.dim }}>{'  -  ' + [r.board, r.division, r.docket_number].filter(Boolean).join(' ')}</span>
          <div style={{ fontSize: 11, color: C.dim }}>
            {[r.subject_code ? 'subject ' + r.subject_code : '', r.subject, r.status].filter(Boolean).join('  -  ')}
          </div>
        </div>
        <div style={{ fontSize: 11, whiteSpace: 'nowrap', textAlign: 'right' }}>
          <div style={{ color: C.dim }}>{r.submission_due ? 'due ' + r.submission_due : 'no due date on record'}</div>
          <div style={{ color: x.filed ? C.ok : C.dim }}>
            {x.filed ? 'filed ' + x.filedDate : 'no filing date on record'}
          </div>
        </div>
      </div>
      {r.record_class_note
        ? <div style={{ fontSize: 10, color: C.dim, marginTop: 4, lineHeight: 1.5 }}>{r.record_class_note}</div>
        : null}
    </div>
  );
}

// T5 / D-18: a discipline case forwarded to him. Clicking opens the case.
function ForwardedCaseRow({ x, onOpen }) {
  const k = x.kase;
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div {...activate(onOpen ? () => onOpen(k.id) : null, 'Open case ' + (k.caseNumber || 'with no number'))}
             style={{ cursor: onOpen ? 'pointer' : 'default' }}>
          <span style={{ color: C.text }}>{k.caseNumber || '(no case number)'}{k.memberName ? '  -  ' + k.memberName : ''}</span>
          <div style={{ fontSize: 11, color: C.dim }}>
            {[k.craft, k.railroad].filter(Boolean).join('  -  ') || 'craft and railroad not on the case'}
            {x.forwardedBy ? '  -  forwarded by ' + x.forwardedBy : ''}
          </div>
        </div>
        <div style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
          {x.forwardUnknown
            ? <span style={{ color: C.dim }}>forward date not on record</span>
            : <span style={{ color: x.forwardedDaysAgo >= 7 ? C.accent : C.dim }}>forwarded {x.forwardedDaysAgo}d ago</span>}
        </div>
      </div>
    </div>
  );
}

export default function GCDesk({ user, onViewClaim, onNavigate, onOpenMember, onViewCase }) {
  const [claims, setClaims] = useState([]);
  const [cases, setCases] = useState([]);
  const [casesError, setCasesError] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // A General Chairperson account with no committee sees NOTHING, and an empty screen would read as
  // "your committee is in good order". Same class of silent-empty failure LCDesk guards against for
  // an unprovisioned Local Chairperson.
  const gcNoCommittee = user?.role === 'general_chair' && !user?.committee;

  useEffect(() => {
    // F-15 (SS201): the query is shaped in src\data\claimQueries.js so that it states what the
    // read rule requires (not a draft) - an unshaped `committee ==` query is refused outright.
    // A Local Chairperson is not handed a committee-wide view by this page: he gets his territory.
    const unsub = subscribeVisibleClaims(db, user, rows => {
      setClaims(rows);
      setLoadError('');
      setLoading(false);
    }, err => {
      console.error('[GCDesk] query failed:', err);
      setLoadError(err && err.code ? err.code + ': ' + err.message : ((err && err.message) || 'Failed to load claims'));
      setLoading(false);
    });
    return unsub;
  }, [user?.uid, user?.role, user?.committee, user?.isAdmin, (user?.scopes || []).join('|')]);

  // T5 / D-18: the committee's discipline cases, through the same shaped queries the case list uses.
  // A refusal is shown on the block, never read as an empty inbox.
  useEffect(() => {
    const unsub = subscribeVisibleCases(db, user, onSnapshot, rows => { setCases(rows); setCasesError(''); },
      err => { console.error('[GCDesk] discipline query failed:', err); setCasesError((err && (err.code || err.message)) || 'refused'); });
    return unsub;
  }, [user?.uid, user?.role, user?.committee, user?.isAdmin, (user?.scopes || []).join('|')]);
  const forwardedCases = useMemo(() => forwardedDisciplineToGC(cases), [cases]);

  // Onboarding one-click (Bob 2026-10-26): an application to join lands here as approve/decline, not a task.
  const [pendingApps, setPendingApps] = useState(0);
  useEffect(() => {
    if (!user || user.role !== 'general_chair' || !user.committee) { setPendingApps(0); return undefined; }
    return onSnapshot(query(collection(db, 'applications'), where('generalCommittee', '==', user.committee), where('status', '==', 'pending')),
      (s) => setPendingApps(s.size), () => setPendingApps(0));
  }, [user?.uid, user?.role, user?.committee]);

  const seats = useSeatsOfRecord(user && user.committee);   // S3: the unrouted queue, the vacant seats and the picker read the spine
  const g = useMemo(() => partitionGC(claims, { roster: seats.roster }), [claims, seats.roster]);
  // Master list item 6: the clocks, the Board stage and the withdrawn claims, across the committee (deskClocks.js).
  const item6 = useMemo(() => ({ clocks: deskClockRows(claims), board: boardRows(claims), withdrawn: withdrawnRows(claims) }), [claims]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: C.dim }}>Loading the committee...</div>;
  }

  if (gcNoCommittee) {
    return (
      <div style={{ maxWidth: 760, margin: '32px auto', padding: 24, border: '1px solid ' + C.accent, background: C.panel }}>
        <div style={{ color: C.accent, fontWeight: 700, marginBottom: 8 }}>This account carries no committee</div>
        <div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>
          Your account holds the General Chairperson role but no committee, so no claim can reach this view.
        </div>
        <div style={{ color: C.dim, fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
          <b>This is not a report that your committee is in good order.</b> It is a report that the
          platform does not know which committee is yours.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ maxWidth: 760, margin: '32px auto', padding: 24, border: '1px solid ' + C.bad, background: C.panel }}>
        <div style={{ color: C.bad, fontWeight: 700, marginBottom: 8 }}>Couldn't load the committee</div>
        <div style={{ color: '#F4A0A0', fontSize: 12, fontFamily: 'monospace' }}>{loadError}</div>
        <div style={{ color: C.dim, fontSize: 12, marginTop: 10 }}>This is a load failure, not an orderly committee.</div>
      </div>
    );
  }

  const open = (id) => onViewClaim && onViewClaim(id);
  const d = g.docket;
  const L = g.load;

  // The landing (2026-10-26, Bob): lead with what actually needs him, name what is clear in one line
  // rather than a stack of empty blocks, and tuck the reference tools behind one control.
  const clocksQuiet = item6.clocks.length === 0 && item6.board.length === 0 && item6.withdrawn.length === 0;
  const docketQuiet = d.overdue.length === 0 && d.dueSoon.length === 0 && d.noDueDate.length === 0;
  const needs = [
    g.elevated.length && { tone: C.bad, label: g.elevated.length + ' claim(s) sent up — waiting on your decision' },
    forwardedCases.length && { tone: C.accent, label: forwardedCases.length + ' discipline case(s) forwarded — your decision' },
    g.unrouted.length && { tone: C.bad, label: g.unrouted.length + ' claim(s) routed nowhere — only you can assign' },
    L.vacantWithWork.length && { tone: C.bad, label: L.vacantWithWork.length + ' vacant seat(s) carrying work' },
    d.overdue.length && { tone: C.bad, label: d.overdue.length + ' arbitration case(s) past their submission date' },
    pendingApps > 0 && { tone: C.accent, label: pendingApps + ' application(s) to join - approve or decline', nav: 'applications' },
  ].filter(Boolean);
  const cleared = [
    !g.unrouted.length && 'nothing routed nowhere',
    clocksQuiet && 'no clocks running, nothing before a Board, nothing withdrawn',
    docketQuiet && 'the arbitration docket is quiet',
  ].filter(Boolean);

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '0 20px 28px' }}>
      {/* WHO YOU ARE (2026-10-26 first-screen redesign, Bob) */}
      <div style={{ padding: '18px 0 2px' }}>
        <h1 style={{ margin: 0, color: C.text, fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}>
          {user?.name || 'General Chairperson'}
        </h1>
        <div style={{ color: C.dim, fontSize: 13, marginTop: 3 }}>
          {user?.name ? 'General Chairperson · ' : ''}{user?.committee || 'this committee'} · {g.total} claim(s) in view
        </div>
      </div>

      {/* NEEDS YOU NOW - lead with what is actually waiting on him; name what is clear in one line */}
      <div style={{ margin: '18px 0 6px', color: C.dim, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>Needs you now</div>
      {needs.length === 0
        ? <div style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid ' + C.line, color: C.ok, fontSize: 14 }}>
            You're all clear - nothing is waiting on your decision right now.
          </div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {needs.map((n, i) => (
              n.nav
                ? <button key={i} type="button" onClick={() => onNavigate && onNavigate(n.nav)}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid ' + n.tone, background: C.panel, cursor: 'pointer', width: '100%', textAlign: 'left', font: 'inherit' }}>
                    <span style={{ flex: 1, color: n.tone, fontSize: 14, fontWeight: 600 }}>{n.label}</span>
                    <span style={{ color: n.tone, fontSize: 12 }}>open &rarr;</span>
                  </button>
                : <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid ' + n.tone, background: C.panel }}>
                    <span style={{ flex: 1, color: n.tone, fontSize: 14, fontWeight: 600 }}>{n.label}</span>
                  </div>
            ))}
          </div>}
      {cleared.length > 0 && (
        <div style={{ marginTop: 8, color: C.dim, fontSize: 12 }}>Clear right now: {cleared.join(' · ')}.</div>
      )}

      {/* THE WORK */}
      <div style={{ margin: '24px 0 6px', color: C.dim, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}>The work - what is falling between the seats</div>
      <GcRequiredReading user={user} />
      <WarRoomActionsDesk user={user} />   {/* section 532: action items from War Room occasions, until done */}

      {/* R-6b: FIRST BLOCK ON THE PAGE, above the unrouted queue and above the panels. A claim a
          Local Chairperson has sent up is waiting on THIS MAN and nobody else, and until today it
          appeared on no screen at all - partitionGC computed it and the page never rendered it. It
          is the one thing here that is somebody else's claim waiting on his decision. */}
      <MemberStandingFlags user={user} />

      <Block title="Sent up to you - waiting on your decision" tone={g.elevated.length ? 'warn' : 'ok'}
             count={g.elevated.length + ' claim(s)'}
             subtitle="A Local Chairperson elevated these to GC Review. At this rung the matter has left his hands - he can neither send it onward nor send it back - so nothing moves until you open it.">
        {g.elevated.length === 0
          ? 'Nothing has been sent up to you.'
          : <>
              <div style={{ marginBottom: 8 }}>
                Soonest filing window first. A claim with <b>no</b> computable window sorts last rather
                than looking urgent or looking safe.
              </div>
              {g.elevated.map(x => (
                <ElevatedRow key={x.claim.docId || x.claim.id} x={x} onOpen={open} />
              ))}
            </>}
      </Block>

      <Block title="Discipline cases forwarded to you - waiting on your decision"
             tone={casesError ? 'bad' : forwardedCases.length ? 'warn' : 'ok'}
             count={casesError ? 'could not load' : forwardedCases.length + ' case(s)'}
             subtitle="A Local Chairperson certified the investigation record and forwarded these. From the forward you hold exclusive authority over the case: the decision, resolving or reopening it.">
        {casesError
          ? 'The discipline cases could not be loaded (' + casesError + '). This is a load failure, not an empty inbox.'
          : forwardedCases.length === 0
            ? 'No discipline case is waiting on you.'
            : <>
                <div style={{ marginBottom: 8 }}>
                  Longest waiting first, counted from the day it was forwarded. A case with no forward date on
                  record is listed first, because it may be the oldest.
                </div>
                {forwardedCases.map(x => (
                  <ForwardedCaseRow key={x.kase.id} x={x} onOpen={onViewCase} />
                ))}
              </>}
      </Block>

      {g.unrouted.length > 0 && (
      <Block title="Routed nowhere - only you can assign these" tone="bad"
             count={g.unrouted.length + ' claim(s)'}
             subtitle="The work location resolved to no seat, so the claim was captured under a committee-level UNROUTED key rather than lost. No Local Chairperson can see it.">
              <div style={{ marginBottom: 8 }}>
                Soonest filing window first. A claim with <b>no</b> computable window sorts last rather
                than looking urgent or looking safe.
              </div>
              {g.unrouted.map(x => (
                <UnroutedRow key={x.claim.docId} x={x} roster={seats.roster} user={user} onOpen={open} />
              ))}
      </Block>
      )}

      <Block title="Seats nobody holds, and what is pointed at them" tone={L.vacantWithWork.length ? 'bad' : 'ok'}
             count={L.vacantWithWork.length + ' vacant seat(s) carrying work'}
             subtitle={<>When a Local Chairperson's seat is vacant, the work and the authority vest in the General Chairperson. The claims below routed perfectly - to an empty chair.<RulingRef user={user} n={60} why="When a Local Chairperson's seat is vacant, the work and authority over its claims and cases belong to the General Chairperson." /></>}>
        {L.vacantWithWork.length === 0
          ? 'No vacant seat has a claim pointed at it.'
          : L.vacantWithWork.map(s => <SeatRow key={s.scopeKey} s={s} />)}

        {L.vacantEmpty.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ color: C.text, marginBottom: 4 }}>Vacant with nothing pointed at them - {L.vacantEmpty.length}</div>
            <div style={{ marginBottom: 6 }}>Still empty, and still needing a holder. An empty seat with no work is not a solved seat.</div>
            {L.vacantEmpty.map(s => <SeatRow key={s.scopeKey} s={s} />)}
          </div>
        )}

        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid ' + C.line }}>
          <b style={{ color: C.text }}>{L.notOfRecord.length} seat(s) are NOT OF RECORD, and that is a different thing.</b>{' '}
          The platform does not record who holds them. Routing depends on an account carrying the scope
          key, not on the roster printing a name, so this is an attestation gap and not a routing hole -
          and it is counted separately for exactly that reason. Folding it into "vacant" would flag
          almost every seat and make the vacancy signal useless on the one seat that is genuinely empty.
        </div>

        {L.offRoster.length > 0 && (
          <div style={{ marginTop: 10, color: C.accent }}>
            <b>{L.offRoster.length} claim(s) carry a scope key that is on no roster row.</b> Not unrouted -
            they resolved to something - but to a seat the roster does not know. That is the routing table
            and the roster disagreeing, and it is a defect in the record rather than a queue to work.
          </div>
        )}
      </Block>

      {!clocksQuiet && <DeskClocksPanel clocks={item6.clocks} board={item6.board} withdrawn={item6.withdrawn} onOpen={open} />}

      <Block title="Committee readiness" tone="neutral" count="state not shown here"
             subtitle="What is not yet stocked: units without rates, seats without holders or locations, agreements without a signed primary document">
        The readiness surface is a page of its own and stays there rather than being copied onto this one -
        a fact kept in two places is a defect, and a second copy of that logic would drift from the first
        inside a week.
        {onNavigate ? <> Open it here:{' '}
          <button type="button" onClick={() => onNavigate('readiness')} style={{ color: C.accent, cursor: 'pointer', fontWeight: 700, background: 'transparent', border: 'none', padding: 0, font: 'inherit', textDecoration: 'underline' }}>Readiness</button>.</> : null}
      </Block>

      <Block title="Arbitration docket" tone={d.overdue.length ? 'bad' : d.dueSoon.length ? 'warn' : 'ok'}
             count={d.watched.length + ' watched, ' + d.notWatched.length + ' not'}
             subtitle={'Generated from data\\YCP_ARBITRATION_DOCKET.csv, which remains the record. Claimant names and carrier claim numbers are withheld from the browser bundle on purpose.'}>
        {d.malformed.length > 0 && (
          <div style={{ color: C.bad, marginBottom: 8 }}>
            <b>{d.malformed.length} row(s) carry a record class that is neither LIVE nor DEAD_FILE.</b> They are
            neither watched nor dismissed, because a row whose class cannot be read is a row whose treatment
            cannot be decided. Fix the CSV and regenerate.
          </div>
        )}

        {d.overdue.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.bad, fontWeight: 700 }}>Submission date passed and no filing date on record - {d.overdue.length}</div>
            {d.overdue.map(x => <DocketRow key={x.row.case_id} x={x} />)}
          </div>
        )}

        {d.dueSoon.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.accent, fontWeight: 700 }}>Due within {DOCKET_SOON_DAYS} days - {d.dueSoon.length}</div>
            {d.dueSoon.map(x => <DocketRow key={x.row.case_id} x={x} />)}
          </div>
        )}

        {d.noDueDate.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.accent, fontWeight: 700 }}>Watched, unfiled, and carrying no due date - {d.noDueDate.length}</div>
            <div>Not overdue and certainly not clear. Listed so the absence is visible rather than blank.</div>
            {d.noDueDate.map(x => <DocketRow key={x.row.case_id} x={x} />)}
          </div>
        )}

        {d.watched.length === 0 && (
          <div style={{ marginBottom: 10 }}>
            <b style={{ color: C.text }}>Nothing on this docket is on a clock.</b> Every row is marked
            DEAD_FILE - out of scope for this platform, not invented and never to be purged.
          </div>
        )}

        {d.notWatched.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid ' + C.line }}>
            <div style={{ color: C.text, fontWeight: 700, marginBottom: 4 }}>
              Not watched - named rather than hidden ({d.notWatched.length})
            </div>
            <div style={{ marginBottom: 6 }}>
              These are <b>real</b> cases, before a real Board, being handled off this platform. They are
              excluded from every clock here and must never be purged. A page that simply omitted them would
              invite the next reader to conclude they were never filed.
            </div>
            {d.notWatched.map(x => <DocketRow key={x.row.case_id} x={x} />)}
          </div>
        )}
      </Block>

      {/* COMMITTEE TOOLS - the reference panels, folded behind one control so the desk opens clean */}
      <details style={{ marginTop: 22 }}>
        <summary style={{ cursor: 'pointer', color: C.text, fontSize: 14, fontWeight: 600, padding: '10px 0' }}>Committee tools</summary>
        <div style={{ marginTop: 8 }}>
          <ClaimTally user={user} mode="matrix" onOpenMember={onOpenMember} />
          <GcAgreementAdmin user={user} />
          <S44DeskPanel claims={claims} user={user} onOpen={open} />
          <AssignmentsPanel user={user} mode="issuer" onOpenClaim={open} />
          <ContinuityPlanPanel user={user} committee={user && user.committee} />
          <LetterheadPanel user={user} committee={user && user.committee} />
          <ServiceSettingsPanel user={user} committee={user && user.committee} />
          <PresentmentDirectoryPanel user={user} mode="gc" />
          <ArbitrariesPanel user={user} committee={user && user.committee} />
          <ProposalCostPanel user={user} committee={user && user.committee} />
          <SignoffPanel user={user} />
          <SeatsPanel user={user} committee={user && user.committee} />
        </div>
      </details>

      <div style={{ color: C.dim, fontSize: 11, lineHeight: 1.6, borderTop: '1px solid ' + C.line, paddingTop: 10 }}>
        Counts and ordering come from <code>src/data/gcDesk.js</code> and are proved by{' '}
        <code>scripts/test-gc-desk.mjs</code>. The reassign control writes three fields -{' '}
        <code>scopeKey</code>, <code>routingStatus</code> and <code>localCommittee</code> - and is
        permitted to a General Chairperson by the rules already deployed; it needs no rules change.
        Every assignment is written into the claim's own note history with your name and your role.
        {onNavigate ? <> The full committee list is on the{' '}
          <button type="button" onClick={() => onNavigate('claims')} style={{ color: C.accent, cursor: 'pointer', background: 'transparent', border: 'none', padding: 0, font: 'inherit', textDecoration: 'underline' }}>Claims</button> page.</> : null}
      </div>
    </div>
  );
}
